// frontend/src/pages/CorrespondencesPageV3.tsx - TABLE/GRID VIEW WITH EXPANDABLE DETAILS
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { MessageSquare } from 'lucide-react';
import { cn, formatDate, truncateText } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import apiClient from '@/api';
// Note: Using Unicode symbols instead of icon library for compatibility
const Icons = {
  ChevronDown: () => <span>▼</span>,
  ChevronUp: () => <span>▲</span>,
  ChevronRight: () => <span>▶</span>,
  MagnifyingGlass: () => <span>🔍</span>,
  Funnel: () => <span>⏳</span>,
  ArrowsUpDown: () => <span>↕</span>,
  PaperClip: () => <span>📎</span>,
  Envelope: () => <span>✉</span>,
  Sparkles: () => <span>✨</span>,
  DocumentText: () => <span>📄</span>,
  Trash: () => <span>🗑</span>,
  ArrowDownTray: () => <span>⬇</span>,
};

// Helper to get a custom field value from correspondence
const getCustomFieldValue = (correspondence: any, fieldId: string): any => {
  const fields = correspondence.customFields || [];
  const field = fields.find((f: any) => f.fieldId === fieldId);
  return field?.value ?? null;
};

// Helper to format custom field value for display
const formatCustomFieldValue = (value: any, type?: string): string => {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (type === 'date' && value) {
    try { return formatDate(new Date(value), false); } catch { return String(value); }
  }
  if (type === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// Unified column definition for the correspondences table
interface GridColumn {
  fieldId: string;
  fieldType: 'system' | 'custom';
  label: string;
  width: 'narrow' | 'medium' | 'wide';
  order: number;
  isVisible: boolean;
  isSortable: boolean;
  isFilterable?: boolean;
  type?: string; // For custom fields: text, number, date, select, multi-select
  options?: Array<{ value: string; label: string; color?: string }>; // For select/multi-select fields
}

// Hook to fetch org customization fields + grid config
const useOrgCustomization = () => {
  const [fields, setFields] = useState<any[]>([]);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [filterableColumns, setFilterableColumns] = useState<GridColumn[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiClient.organizationCustomization.getFields().catch(() => null),
      apiClient.organizationCustomization.getGridConfig('correspondenceGrid').catch(() => null)
    ]).then(([fieldsRes, gridRes]) => {
      if (cancelled) return;

      const fieldsData = fieldsRes?.data || fieldsRes;
      const customFields = fieldsData?.effectiveCorrespondenceFields || [];
      setFields(customFields);

      // Build unified column list
      const gridData = gridRes?.data || gridRes;
      const systemCols: GridColumn[] = (gridData?.columns || []).map((c: any) => ({
        fieldId: c.fieldId,
        fieldType: 'system' as const,
        label: c.label,
        width: c.width || 'medium',
        order: c.order || 99,
        isVisible: c.isVisible !== false,
        isSortable: c.isSortable !== false,
        isFilterable: c.isFilterable === true,
      }));

      // Add custom fields with showInListView as columns (after system columns)
      // Also include filterable custom fields even if not in list view
      const customCols: GridColumn[] = customFields
        .filter((f: any) => f.display?.showInListView)
        .map((f: any) => ({
          fieldId: f.fieldId,
          fieldType: 'custom' as const,
          label: f.name,
          width: f.display?.tableColumnWidth || 'medium',
          order: 100 + (f.display?.order || 99), // Custom fields after system
          isVisible: true,
          isSortable: false,
          isFilterable: f.type === 'select' || f.type === 'multi-select' || f.type === 'boolean',
          type: f.type,
          options: f.options || [],
        }));

      // Merge, filter visible, sort by order
      // Hide custom fields that are already covered by system columns
      const hiddenCustomFieldIds = ['document_subject', 'received_from', 'addressed_to']; // Covered by system 'subject'/'from'/'to' columns
      const allCols = [...systemCols, ...customCols]
        .filter(c => c.isVisible && !hiddenCustomFieldIds.includes(c.fieldId))
        .sort((a, b) => a.order - b.order);

      setColumns(allCols);

      // Build filterable fields list: system filterable columns + custom select/multi-select fields
      const filterableCols: GridColumn[] = [];

      // System filterable columns from grid config
      systemCols.filter(c => c.isFilterable).forEach(c => filterableCols.push(c));

      // All custom fields that are select/multi-select (filterable regardless of showInListView)
      customFields
        .filter((f: any) => f.type === 'select' || f.type === 'multi-select' || f.type === 'boolean')
        .forEach((f: any) => {
          // Only add if not already in filterable list
          if (!filterableCols.some(c => c.fieldId === f.fieldId)) {
            filterableCols.push({
              fieldId: f.fieldId,
              fieldType: 'custom',
              label: f.name,
              width: 'medium',
              order: 100 + (f.display?.order || 99),
              isVisible: false,
              isSortable: false,
              isFilterable: true,
              type: f.type,
              options: f.options || [],
            });
          }
        });

      setFilterableColumns(filterableCols);
    });
    return () => { cancelled = true; };
  }, []);

  return { fields, columns, filterableColumns };
};

// Resolve cell value for a column from a correspondence row
const resolveCellValue = (correspondence: any, col: GridColumn, projects: any[]): string => {
  if (col.fieldType === 'custom') {
    const cf = (correspondence.customFields || []).find((f: any) => f.fieldId === col.fieldId);
    if (!cf || cf.value === null || cf.value === undefined) return '-';
    return formatCustomFieldValue(cf.value, col.type);
  }

  // System fields
  switch (col.fieldId) {
    case 'subject': {
      // Prefer user-entered Document Subject from custom fields, fall back to email subject
      const docSubject = (correspondence.customFields || []).find((f: any) => f.fieldId === 'document_subject');
      return (docSubject?.value) || correspondence.subject || 'No Subject';
    }
    case 'from': {
      const from = correspondence.from || {};
      return from.name || from.email || 'Unknown';
    }
    case 'documentDate': return formatDate(correspondence.documentDate || correspondence.createdAt, false) || '-';
    case 'documentType': return correspondence.documentType || '-';
    case 'projectName': {
      // projectId may be populated as an object { _id, projectName, projectCode } or a string ID
      const pid = correspondence.projectId;
      if (pid && typeof pid === 'object') {
        return pid.projectName || pid.name || '-';
      }
      // Fallback: also check projectRelationship.projectId
      const prPid = correspondence.projectRelationship?.projectId;
      if (prPid && typeof prPid === 'object') {
        return prPid.projectName || prPid.name || '-';
      }
      // Last resort: lookup from projects array by string ID
      const idStr = pid || prPid;
      if (idStr) {
        return projects.find((p: any) => p._id === idStr || p.id === idStr)?.name || '-';
      }
      return '-';
    }
    case 'attachmentCount': {
      const count = getAttachmentCount(correspondence);
      return count > 0 ? String(count) : '-';
    }
    case 'correspondenceStatus': {
      const raw = correspondence.correspondenceStatus || correspondence.status;
      if (!raw) return '-';
      const labels: Record<string, string> = {
        open: 'Open',
        partially_closed: 'Partial',
        closed: 'Closed'
      };
      return labels[raw] || raw;
    }
    case 'responseStatus': {
      // Check responseSummary first (populated on list endpoint)
      const rs = correspondence.responseSummary;
      if (rs && rs.responseCount > 0) {
        return `${rs.responseCount} response${rs.responseCount > 1 ? 's' : ''}`;
      }
      // Fallback to populated responseId
      if (correspondence.responseId && typeof correspondence.responseId === 'object') {
        return (correspondence.responseId as any).status || '-';
      }
      return '-';
    }
    case 'documentCode': return correspondence.documentCode || '-';
    case 'documentReference': return correspondence.documentReference || '-';
    case 'source': return correspondence.source || correspondence.documentType || '-';
    case 'to': {
      const to = correspondence.to;
      if (Array.isArray(to) && to.length > 0) {
        return to.map((t: any) => t?.name || t?.email || t).filter(Boolean).join(', ') || '-';
      }
      if (typeof to === 'string') return to;
      return '-';
    }
    case 'createdAt': return formatDate(correspondence.createdAt) || '-';
    default: return '-';
  }
};

// Helper function to get attachment count from either V3 or legacy format
const getAttachmentCount = (correspondence: any): number => {
  // V3 format: attachmentCount field
  if (typeof correspondence.attachmentCount === 'number') {
    return correspondence.attachmentCount;
  }
  // Legacy format: attachments array
  if (Array.isArray(correspondence.attachments)) {
    return correspondence.attachments.length;
  }
  return 0;
};

import {
  useCorrespondences,
  useUpdateCorrespondence,
  useDeleteCorrespondence,
  useCorrespondenceAttachments,
  useCorrespondenceAI
} from '@/hooks/useCorrespondences';

import { useAllProjects } from '@/hooks/useProjects';
import type { Correspondence, Project } from '@/api/types';
import { CreateResponseModal } from '@/components/responses/CreateResponseModal';
import apiClient from '@/api';

// ===== TYPES =====

type SortField = 'subject' | 'from' | 'documentDate' | 'projectName' | 'documentType' | 'createdAt' | string;
type SortOrder = 'asc' | 'desc';

interface TableFilters {
  search: string;
  projectId: string;
  documentType: string;
  hasAttachments: string;
  hasProject: string;
  direction: string;
  correspondenceStatus: string;
  [key: string]: string; // Dynamic custom field filters (cf_fieldId)
}

// ===== UTILITY FUNCTIONS =====

function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ===== TABLE HEADER COMPONENT =====

const TableHeader: React.FC<{
  field: string;
  label: string;
  currentField: string;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
}> = ({ field, label, currentField, currentOrder, onSort, className }) => {
  const isActive = currentField === field;

  return (
    <th
      className={cn(
        'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
        className
      )}
      onClick={() => onSort(field)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(field); } }}
      tabIndex={0}
      role="columnheader"
      aria-sort={isActive ? (currentOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isActive ? (
          currentOrder === 'asc' ? (
            <Icons.ChevronUp />
          ) : (
            <Icons.ChevronDown />
          )
        ) : (
          <Icons.ArrowsUpDown />
        )}
      </div>
    </th>
  );
};

// ===== STATUS BADGE =====

const StatusBadge: React.FC<{
  manual?: boolean;
  hasProject?: boolean;
  documentType?: string;
}> = ({ manual, hasProject, documentType }) => {
  return (
    <div className="flex items-center space-x-1">
      {manual && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Manual
        </span>
      )}
      {hasProject && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Project
        </span>
      )}
      {documentType && (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          {documentType}
        </span>
      )}
    </div>
  );
};

// ===== ATTACHMENT POPOVER (paperclip click) =====

const AttachmentPopover: React.FC<{
  correspondenceId: string;
  count: number;
}> = ({ correspondenceId, count }) => {
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<{ id: string; text: string } | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; openUp: boolean }>({ top: 0, left: 0, openUp: false });
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  // Close on outside click (check both popover and button)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setOpen(false);
        setPreviewContent(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setPreviewContent(null); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleOpen = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't toggle row expansion
    if (open) { setOpen(false); setPreviewContent(null); return; }
    // Position popover relative to button using fixed coordinates
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const openUp = rect.bottom + 400 > window.innerHeight;
      setPopoverPos({
        top: openUp ? rect.top : rect.bottom + 4,
        left: rect.right,
        openUp,
      });
    }
    setOpen(true);
    if (!attachments) {
      setLoading(true);
      try {
        const res = await apiClient.correspondences.getAttachments(correspondenceId);
        const data = (res as any)?.data ?? (res as any)?.attachments ?? res;
        // Deduplicate by filename+size (backend may return same file from multiple sources)
        const arr = Array.isArray(data) ? data : [];
        const seen = new Set<string>();
        setAttachments(arr.filter((a: any) => {
          const key = `${a.filename}|${a.size || 0}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      } catch {
        setAttachments([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDownload = async (attachmentId: string, filename: string, contentType: string | undefined, e: React.MouseEvent, forceInline?: boolean) => {
    e.stopPropagation();
    setDownloading(attachmentId);
    try {
      const isPdf = contentType?.includes('pdf') || filename.toLowerCase().endsWith('.pdf');
      const useInline = forceInline || isPdf;
      const res = await apiClient.attachments.getPresignedUrl(attachmentId, useInline ? { inline: true } : undefined);
      const url = (res as any)?.data?.presignedUrl ?? (res as any)?.presignedUrl;
      if (url) {
        if (useInline) {
          // Open in browser tab (inline disposition)
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          // Download other file types
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }
    } catch { /* ignore */ }
    finally { setDownloading(null); }
  };

  const handlePreview = (attachment: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (previewContent?.id === attachment._id) {
      setPreviewContent(null);
    } else if (attachment.extractedContent) {
      setPreviewContent({ id: attachment._id, text: attachment.extractedContent });
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const popoverWidth = previewContent ? 448 : 320; // 28rem / 20rem

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={cn(
          "flex-shrink-0 flex items-center gap-1 px-1.5 py-1 rounded transition-colors",
          open
            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
            : "text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20"
        )}
        title={`${count} attachment${count !== 1 ? 's' : ''} — click to preview`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Icons.PaperClip />
        <span className="text-xs font-medium">{count}</span>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{
            width: popoverWidth,
            maxHeight: '24rem',
            top: popoverPos.openUp ? undefined : popoverPos.top,
            bottom: popoverPos.openUp ? (window.innerHeight - popoverPos.top + 4) : undefined,
            left: Math.max(8, popoverPos.left - popoverWidth),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              Attachments ({count})
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); setPreviewContent(null); }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm leading-none"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: previewContent ? '20rem' : '18rem' }}>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading...
              </div>
            ) : attachments && attachments.length > 0 ? (
              <div>
                {attachments.map((att: any) => {
                  const hasOCR = !!att.extractedContent;
                  const isPreviewing = previewContent?.id === att._id;
                  return (
                    <div key={att._id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        {/* File icon */}
                        <div className="flex-shrink-0 w-8 h-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          {att.contentType?.includes('pdf') ? (
                            <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                          ) : att.contentType?.includes('image') ? (
                            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" /></svg>
                          ) : (
                            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                          )}
                        </div>
                        {/* File info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate" title={att.filename}>
                            {att.filename}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatSize(att.size || 0)}
                            {hasOCR && <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">• OCR</span>}
                          </div>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Preview button for PDFs — opens in new tab */}
                          {(att.contentType?.includes('pdf') || att.filename?.toLowerCase().endsWith('.pdf')) && (
                            <button
                              onClick={(e) => handleDownload(att._id, att.filename, att.contentType, e)}
                              disabled={downloading === att._id}
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                              title="Preview PDF in new tab"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </button>
                          )}
                          {/* OCR text button */}
                          {hasOCR && (
                            <button
                              onClick={(e) => handlePreview(att, e)}
                              className={cn(
                                "p-1.5 rounded text-xs transition-colors",
                                isPreviewing
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                  : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-emerald-400 dark:hover:bg-emerald-900/20"
                              )}
                              title="View OCR extracted text"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                          )}
                          {/* Download button (for non-PDFs) */}
                          {!(att.contentType?.includes('pdf') || att.filename?.toLowerCase().endsWith('.pdf')) && (
                          <button
                            onClick={(e) => handleDownload(att._id, att.filename, att.contentType, e)}
                            disabled={downloading === att._id}
                            className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:text-green-400 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                            title="Download"
                          >
                            {downloading === att._id ? (
                              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            )}
                          </button>
                          )}
                        </div>
                      </div>
                      {/* OCR preview panel */}
                      {isPreviewing && previewContent && (
                        <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Extracted Text</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(previewContent.text);
                                const btn = e.currentTarget;
                                const orig = btn.textContent;
                                btn.textContent = 'Copied!';
                                setTimeout(() => { btn.textContent = orig; }, 1500);
                              }}
                              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                            >
                              Copy
                            </button>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {previewContent.text.split(/(?=---\s*Page\s+\d+\s*---)/).map((section: string, sIdx: number) => {
                              const pageMatch = section.match(/^---\s*Page\s+(\d+)\s*---\n?/);
                              const content = pageMatch ? section.replace(/^---\s*Page\s+\d+\s*---\n?/, '') : section;
                              const trimmed = content.replace(/\n{3,}/g, '\n\n').trim();
                              if (!trimmed) return null;
                              return (
                                <div key={sIdx}>
                                  {pageMatch && (
                                    <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0">
                                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Page {pageMatch[1]}</span>
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-700 dark:text-gray-300 px-3 py-2 whitespace-pre-wrap leading-relaxed">
                                    {trimmed}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-gray-500">No attachments found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ===== EDITABLE CUSTOM FIELDS =====

const EditableCustomFields: React.FC<{
  correspondence: Correspondence;
  orgCustomFields: any[];
  onSave: (fields: any[]) => void;
}> = ({ correspondence, orgCustomFields, onSave }) => {
  const existingFields = (correspondence as any).customFields || [];
  // Show all org-defined fields (not just ones with values) so user can fill blanks
  const editableFields = orgCustomFields.filter((f: any) => f.fieldId !== 'document_age_days');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    // Initialize draft from existing values
    const initial: Record<string, any> = {};
    editableFields.forEach((fd: any) => {
      const existing = existingFields.find((cf: any) => cf.fieldId === fd.fieldId);
      initial[fd.fieldId] = existing?.value ?? fd.defaultValue ?? (fd.type === 'multi-select' ? [] : '');
    });
    setDraft(initial);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const fields = editableFields.map((fd: any) => ({
      fieldId: fd.fieldId,
      name: fd.name,
      type: fd.type,
      value: draft[fd.fieldId] ?? null,
    }));
    await onSave(fields);
    setSaving(false);
    setEditing(false);
  };

  const updateField = (fieldId: string, value: any) => {
    setDraft(prev => ({ ...prev, [fieldId]: value }));
  };

  const renderFieldInput = (fd: any) => {
    const val = draft[fd.fieldId];
    const baseClass = "w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500";

    switch (fd.type) {
      case 'select':
        return (
          <select value={val || ''} onChange={(e) => updateField(fd.fieldId, e.target.value)} className={baseClass}>
            <option value="">—</option>
            {(fd.options || []).map((opt: any) => (
              <option key={opt.value} value={opt.value}>{opt.label || opt.value}</option>
            ))}
          </select>
        );
      case 'multi-select': {
        const selected = Array.isArray(val) ? val : [];
        return (
          <div className="flex flex-wrap gap-1">
            {(fd.options || []).map((opt: any) => {
              const isSelected = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    const next = isSelected ? selected.filter((v: string) => v !== opt.value) : [...selected, opt.value];
                    updateField(fd.fieldId, next);
                  }}
                  className={cn(
                    "px-1.5 py-0.5 rounded text-xs border transition-colors",
                    isSelected
                      ? "bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300"
                      : "bg-gray-50 border-gray-200 text-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600"
                  )}
                >
                  {opt.label || opt.value}
                </button>
              );
            })}
          </div>
        );
      }
      case 'date':
        return <input type="date" value={val ? String(val).substring(0, 10) : ''} onChange={(e) => updateField(fd.fieldId, e.target.value)} className={baseClass} />;
      case 'number':
      case 'currency':
        return <input type="number" value={val ?? ''} onChange={(e) => updateField(fd.fieldId, e.target.value ? Number(e.target.value) : '')} className={baseClass} step="any" />;
      case 'boolean':
        return (
          <select value={val === true ? 'true' : val === false ? 'false' : ''} onChange={(e) => updateField(fd.fieldId, e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)} className={baseClass}>
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      default: // text, url, email, phone
        return <input type="text" value={val ?? ''} onChange={(e) => updateField(fd.fieldId, e.target.value)} className={baseClass} placeholder={fd.placeholder || ''} />;
    }
  };

  if (editableFields.length === 0) return null;

  // Read-only view
  if (!editing) {
    const filledFields = existingFields.filter((cf: any) => cf.value !== null && cf.value !== undefined && cf.value !== '' && cf.fieldId !== 'document_age_days');
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Custom Fields</h4>
          <button
            onClick={startEditing}
            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Edit
          </button>
        </div>
        {filledFields.length > 0 ? (
          <div className="space-y-1 text-xs">
            {filledFields.map((cf: any) => {
              const fieldDef = orgCustomFields.find((f: any) => f.fieldId === cf.fieldId);
              const fieldType = fieldDef?.type || cf.type;
              return (
                <div key={cf.fieldId} className="flex justify-between gap-2">
                  <span className="text-gray-500 dark:text-gray-400 truncate">{cf.name}:</span>
                  <span className="text-gray-900 dark:text-white text-right font-medium truncate max-w-[60%]">
                    {formatCustomFieldValue(cf.value, fieldType)}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No custom fields set. Click Edit to add.</p>
        )}
      </div>
    );
  }

  // Editing view
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Custom Fields</h4>
        <div className="flex gap-1.5">
          <button
            onClick={() => setEditing(false)}
            className="px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {editableFields.map((fd: any) => (
          <div key={fd.fieldId}>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              {fd.name}{fd.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {renderFieldInput(fd)}
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== EXPANDABLE ROW DETAILS =====

const ExpandedRowDetails: React.FC<{
  correspondence: Correspondence;
  projects: Project[];
  orgCustomFields: any[];
  onUpdateProject: (id: string, projectId: string | null) => void;
  onUpdateCustomFields: (id: string, customFields: any[]) => void;
  onUpdateDirection: (id: string, direction: 'inward' | 'outward' | 'internal') => void;
  onUpdateStatus: (id: string, status: 'open' | 'partially_closed' | 'closed') => void;
  onDelete: (id: string) => void;
  onCreateResponse: (correspondence: Correspondence) => void;
}> = ({ correspondence, projects, orgCustomFields, onUpdateProject, onUpdateCustomFields, onUpdateDirection, onUpdateStatus, onDelete, onCreateResponse }) => {
  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [expandedAttachmentId, setExpandedAttachmentId] = useState<string | null>(null);
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();
  const { confirm } = useConfirmation();
  const navigate = useNavigate();

  const correspondenceId = correspondence._id || '';

  // Check if user has permission to delete
  // Allow: admins, implementors, or the user who created the correspondence
  // Handle createdBy being either a string (ID) or populated object
  const createdById = typeof correspondence.createdBy === 'string'
    ? correspondence.createdBy
    : (correspondence.createdBy as any)?._id || (correspondence.createdBy as any)?.id;

  const canDelete = user?.role === 'admin' || user?.role === 'implementor' ||
                    (user?.role === 'contributor' && createdById === user?._id);

  // Debug logging for delete permission
  React.useEffect(() => {
  }, [user, canDelete, correspondence.createdBy, createdById]);

  // Display role badge for debugging
  if (!user) {
    console.warn('User not loaded in ExpandedRowDetails');
  }
  const from = (correspondence as any).from || { email: 'Unknown', name: 'Unknown' };
  const to = (correspondence as any).to || [];
  const cc = (correspondence as any).cc || [];
  const rawContent = correspondence.content || 'No content available';
  // Strip HTML tags for plain text preview in expanded row
  const content = rawContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || 'No content available';

  // V3: Fetch attachments and AI summary on demand when row is expanded
  const { data: attachments, loading: loadingAttachments, error: attachmentsError } = useCorrespondenceAttachments(
    getAttachmentCount(correspondence) > 0 ? correspondenceId : null
  );
  const { data: aiSummary, loading: loadingAI } = useCorrespondenceAI(
    (correspondence as any).hasAISummary ? correspondenceId : null
  );

  // Debug logging for attachments
  React.useEffect(() => {
    if (getAttachmentCount(correspondence) > 0) {
    }
  }, [correspondenceId, attachments, loadingAttachments, attachmentsError]);

  // Handle attachment download
  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    try {
      const blob = await apiClient.attachments.download(attachmentId);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Failed to download attachment:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      addNotification({ type: 'error', title: 'Download Failed', message: errorMsg });
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 sticky left-0 overflow-x-auto" style={{ width: 'calc(100vw - var(--sidebar-width, 240px) - 4rem)', maxWidth: 'calc(100vw - 200px)' }}>
      <div className="px-6 py-4">
      {/* Top bar: Subject + Actions */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {correspondence.subject || 'No Subject'}
            </h4>
            {(correspondence as any).documentCode && (
              <span className="flex-shrink-0 px-2 py-0.5 text-xs font-mono font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded border border-blue-200 dark:border-blue-800">
                {(correspondence as any).documentCode}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
            <span>{from.name ? `${from.name} <${from.email}>` : from.email}</span>
            <span>•</span>
            <span>{formatDate((correspondence as any).documentDate || correspondence.createdAt)}</span>
            {to.length > 0 && (
              <>
                <span>•</span>
                <span className="truncate">To: {to.map((t: any) => t.email || t).join(', ')}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={() => navigate(`/correspondences/${correspondenceId}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            <span>👁️</span>
            <span className="ml-1">View</span>
          </Button>
          <Button
            onClick={() => onCreateResponse(correspondence)}
            size="sm"
          >
            <Icons.DocumentText />
            <span className="ml-1">Respond</span>
          </Button>
          {canDelete && (
            <Button
              variant="secondary"
              onClick={async () => {
                const confirmed = await confirm({
                  title: 'Delete Correspondence',
                  message: 'Delete this correspondence? This cannot be undone.',
                  confirmText: 'Delete',
                  variant: 'danger',
                });
                if (confirmed) {
                  onDelete(correspondenceId);
                }
              }}
              className="text-red-600 hover:text-red-700"
              size="sm"
            >
              <Icons.Trash />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column: Content + Attachments */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Content</h4>
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto">
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{content}</p>
            </div>
          </div>

          {/* Attachments */}
          {getAttachmentCount(correspondence) > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Icons.PaperClip />
                <span className="ml-1">Attachments ({getAttachmentCount(correspondence)})</span>
              </h4>
              {loadingAttachments ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading attachments...</div>
              ) : attachmentsError ? (
                <div className="text-sm text-red-600 dark:text-red-400">
                  Error loading attachments: {attachmentsError instanceof Error ? attachmentsError.message : 'Unknown error'}
                </div>
              ) : attachments && attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((attachment: any, idx: number) => {
                    const isExpanded = expandedAttachmentId === attachment._id;
                    const hasOCR = !!(attachment.extractedContent || attachment.ocrData?.extractedSubject);

                    return (
                      <div
                        key={attachment._id || idx}
                        className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {attachment.filename}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {attachment.contentType} • {(attachment.size / 1024).toFixed(1)} KB
                            </div>
                            {hasOCR && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                <Icons.DocumentText /> OCR Available
                                {attachment.ocrData?.confidence && (
                                  <span className="ml-1">({Math.round(attachment.ocrData.confidence * 100)}% confidence)</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                // Use the correspondence attachment _id directly
                                handleDownloadAttachment(attachment._id, attachment.filename);
                              }}
                              className="text-xs whitespace-nowrap"
                            >
                              Download
                            </Button>
                            {hasOCR && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setExpandedAttachmentId(isExpanded ? null : attachment._id)}
                                className="text-xs whitespace-nowrap"
                              >
                                {isExpanded ? 'Hide' : 'Preview'}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* OCR Content Preview */}
                        {isExpanded && hasOCR && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                Extracted Text Preview
                              </div>
                              <button
                                onClick={() => {
                                  const text = attachment.extractedContent || attachment.ocrData?.extractedSubject || '';
                                  navigator.clipboard.writeText(text);
                                  const btn = document.getElementById(`copy-btn-v3-${attachment._id}`);
                                  if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }
                                }}
                                id={`copy-btn-v3-${attachment._id}`}
                                className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 transition-colors"
                              >
                                Copy
                              </button>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900 rounded max-h-96 overflow-y-auto">
                              {(attachment.extractedContent || attachment.ocrData?.extractedSubject || '').split(/(?=---\s*Page\s+\d+\s*---)/).map((section: string, sIdx: number) => {
                                const pageMatch = section.match(/^---\s*Page\s+(\d+)\s*---\n?/);
                                const content = pageMatch ? section.replace(/^---\s*Page\s+\d+\s*---\n?/, '') : section;
                                const trimmed = content.replace(/\n{3,}/g, '\n\n').trim();
                                if (!trimmed) return null;
                                return (
                                  <div key={sIdx}>
                                    {pageMatch && (
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0">
                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                          Page {pageMatch[1]}
                                        </span>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-700 dark:text-gray-300 p-3 whitespace-pre-wrap leading-relaxed">
                                      {trimmed}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {attachment.ocrData?.extractedSubject && (
                              <div className="mt-2">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Subject:</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">{attachment.ocrData.extractedSubject}</div>
                              </div>
                            )}
                            {attachment.ocrData?.documentReference && (
                              <div className="mt-2">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Reference:</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">{attachment.ocrData.documentReference}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No attachment details available
                </div>
              )}
            </div>
          )}

          {/* Responses */}
          {(() => {
            const rs = (correspondence as any).responseSummary;
            const responses: Array<{ responseId: any; subject?: string; status?: string; currentStage?: string; createdAt?: string; isPrimary?: boolean }> = rs?.responses || [];
            if (responses.length === 0) return null;
            return (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <Icons.DocumentText />
                  <span className="ml-1">Responses ({responses.length})</span>
                </h4>
                <div className="space-y-2">
                  {responses.map((r: any) => {
                    const id = r.responseId?._id || r.responseId;
                    const statusColor = r.status === 'Approved' || r.status === 'approved' || r.status === 'closed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : r.status === 'Rejected' || r.status === 'rejected'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
                    return (
                      <div
                        key={id}
                        className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                        onClick={() => navigate(`/responses/${id}`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {r.subject || 'Untitled Response'}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn('inline-flex px-1.5 py-0.5 rounded text-xs font-medium', statusColor)}>
                                {r.status || 'Draft'}
                              </span>
                              {r.currentStage && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Stage: {r.currentStage}
                                </span>
                              )}
                              {r.isPrimary && (
                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Primary</span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 flex-shrink-0">
                            {r.createdAt ? formatDate(r.createdAt) : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* AI Summary */}
          {(correspondence as any).hasAISummary && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Icons.Sparkles />
                <span className="ml-1">AI Summary</span>
              </h4>
              {loadingAI ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading AI summary...</div>
              ) : aiSummary ? (
                <div className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                  {aiSummary.summary && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {aiSummary.summary}
                    </p>
                  )}
                  {aiSummary.keyTopics && aiSummary.keyTopics.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Key Topics:</div>
                      <div className="flex flex-wrap gap-1">
                        {aiSummary.keyTopics.map((topic: string, idx: number) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded"
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {aiSummary.sentiment && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      Sentiment: <span className="font-medium">{aiSummary.sentiment.overall || 'N/A'}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No AI summary available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Project + Metadata + Custom Fields */}
        <div className="space-y-4 min-w-0 overflow-hidden">
          {/* Project Assignment */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Project</h4>
            <select
              value={(() => {
                // Prefer the legacy projectId field; fall back to projectRelationship.projectId
                // for correspondences (e.g. auto-created outward letters) that only set the new field.
                const pid = (correspondence as any).projectId;
                if (pid) return typeof pid === 'object' ? (pid._id || '') : pid;
                const prPid = (correspondence as any).projectRelationship?.projectId;
                if (prPid) return typeof prPid === 'object' ? (prPid._id || '') : prPid;
                return '';
              })()}
              onChange={(e) => onUpdateProject(correspondenceId, e.target.value || null)}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Personal (no project)</option>
              {projects.filter(p => p.isActive !== false).map(project => (
                <option key={project._id || project.id} value={project._id || project.id}>
                  {(project as any).projectName || project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Direction */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Direction</h4>
            <select
              value={(correspondence as any).direction || 'inward'}
              onChange={(e) => onUpdateDirection(correspondenceId, e.target.value as 'inward' | 'outward' | 'internal')}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="inward">Inward (Received)</option>
              <option value="outward">Outward (Sent)</option>
              <option value="internal">Internal</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Status</h4>
            <select
              value={(correspondence as any).correspondenceStatus || 'open'}
              onChange={(e) => onUpdateStatus(correspondenceId, e.target.value as 'open' | 'partially_closed' | 'closed')}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="open">Open</option>
              <option value="partially_closed">Partially Closed</option>
              <option value="closed">Closed</option>
            </select>
            {(correspondence as any).correspondenceStatusNote && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                {(correspondence as any).correspondenceStatusNote}
              </p>
            )}
          </div>

          {/* Metadata */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Info</h4>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Created:</span>
                <span className="text-gray-900 dark:text-white">{formatDate(correspondence.createdAt)}</span>
              </div>
              {(correspondence as any).hasEmailContent && (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <Icons.Envelope />
                  <span className="ml-1">Email preserved</span>
                </div>
              )}
            </div>
          </div>

          {/* Editable Custom Fields */}
          <EditableCustomFields
            correspondence={correspondence}
            orgCustomFields={orgCustomFields}
            onSave={(fields) => onUpdateCustomFields(correspondenceId, fields)}
          />
        </div>
      </div>
      </div>
    </div>
  );
};

// ===== TABLE ROW COMPONENT =====

const TableRow: React.FC<{
  correspondence: Correspondence;
  projects: Project[];
  orgCustomFields: any[];
  gridColumns: GridColumn[];
  isExpanded: boolean;
  onToggle: () => void;
  onUpdateProject: (id: string, projectId: string | null) => void;
  onUpdateCustomFields: (id: string, customFields: any[]) => void;
  onUpdateDirection: (id: string, direction: 'inward' | 'outward' | 'internal') => void;
  onUpdateStatus: (id: string, status: 'open' | 'partially_closed' | 'closed') => void;
  onDelete: (id: string) => void;
  onCreateResponse: (correspondence: Correspondence) => void;
}> = ({ correspondence, projects, orgCustomFields, gridColumns, isExpanded, onToggle, onUpdateProject, onUpdateCustomFields, onUpdateDirection, onUpdateStatus, onDelete, onCreateResponse }) => {
  const navigate = useNavigate();
  const from = (correspondence as any).from || { email: 'Unknown' };
  const pid = (correspondence as any).projectId;
  const prPid = (correspondence as any).projectRelationship?.projectId;
  const projectName = (pid && typeof pid === 'object' ? (pid.projectName || pid.name) : null)
    || (prPid && typeof prPid === 'object' ? (prPid.projectName || prPid.name) : null)
    || projects.find(p => p._id === (pid || prPid) || p.id === (pid || prPid))?.name
    || '-';

  // Extract key custom field values for inline display (when grid columns not loaded yet)
  const hasGridColumns = gridColumns.length > 0;
  const docRef = !hasGridColumns ? getCustomFieldValue(correspondence, 'document_reference_number') : null;
  const directionValue = (correspondence as any).direction;
  const entryPoint = !hasGridColumns ? getCustomFieldValue(correspondence, 'entry_point') : null;
  const issueTags = !hasGridColumns ? getCustomFieldValue(correspondence, 'issue_tags') : null;
  const receivedFrom = !hasGridColumns ? getCustomFieldValue(correspondence, 'received_from') : null;
  const addressedTo = !hasGridColumns ? getCustomFieldValue(correspondence, 'addressed_to') : null;

  return (
    <>
      <tr
        className={cn(
          'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer',
          isExpanded && 'bg-blue-50 dark:bg-blue-900/10'
        )}
        onClick={onToggle}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center text-gray-400">
            {isExpanded ? (
              <Icons.ChevronDown />
            ) : (
              <Icons.ChevronRight />
            )}
          </div>
        </td>
        <td className="px-6 py-4 align-top min-w-[280px] max-w-[460px]">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div
                className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 break-words"
                title={(() => {
                  const docSubject = (correspondence.customFields || []).find((f: any) => f.fieldId === 'document_subject');
                  return (docSubject?.value) || correspondence.subject || 'No Subject';
                })()}
              >
                {(() => {
                  const docSubject = (correspondence.customFields || []).find((f: any) => f.fieldId === 'document_subject');
                  return (docSubject?.value) || correspondence.subject || 'No Subject';
                })()}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <StatusBadge
                  manual={correspondence.manual}
                  hasProject={!!correspondence.projectId}
                  documentType={(correspondence as any).documentType}
                />

                {/* Response status badge */}
                {correspondence.responseId && typeof correspondence.responseId === 'object' && (() => {
                  const response = correspondence.responseId as any;
                  const getStatusBadge = (status: string) => {
                    switch (status) {
                      case 'draft':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Draft Response
                        </span>;
                      case 'in_review':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          In Review
                        </span>;
                      case 'approved':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Approved
                        </span>;
                      case 'sent':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Sent
                        </span>;
                      case 'scheduled':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Scheduled
                        </span>;
                      case 'cancelled':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Cancelled
                        </span>;
                      default:
                        return null;
                    }
                  };

                  return (
                    <>
                      {getStatusBadge(response.status)}
                      {response.draftLock?.isLocked && response.draftLock.lockedBy && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300" title={`Being edited by ${response.draftLock.lockedBy.username || response.draftLock.lockedBy.email}`}>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Editing
                        </span>
                      )}
                      {response.sentAt && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300" title={`Sent on ${new Date(response.sentAt).toLocaleString()}`}>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {formatDate(response.sentAt, false)}
                        </span>
                      )}
                    </>
                  );
                })()}

                {/* Custom field badges - compact inline */}
                {docRef && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 max-w-[120px] truncate" title={`Ref: ${docRef}`}>
                    {docRef}
                  </span>
                )}
                {directionValue && (
                  <span className={cn(
                    "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium",
                    directionValue === 'inward'
                      ? "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300"
                      : directionValue === 'outward'
                      ? "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                  )}>
                    {directionValue === 'inward' ? 'IN' : directionValue === 'outward' ? 'OUT' : 'INT'}
                  </span>
                )}
                {receivedFrom && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{receivedFrom} → {addressedTo || '?'}</span>
                )}
                {Array.isArray(issueTags) && issueTags.length > 0 && issueTags.slice(0, 2).map((tag: string) => (
                  <span key={tag} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                    {tag}
                  </span>
                ))}
                {Array.isArray(issueTags) && issueTags.length > 2 && (
                  <span className="text-xs text-gray-400">+{issueTags.length - 2}</span>
                )}
              </div>
            </div>
            {getAttachmentCount(correspondence) > 0 && (
              <AttachmentPopover
                correspondenceId={correspondence._id || ''}
                count={getAttachmentCount(correspondence)}
              />
            )}
          </div>
        </td>
        {/* Dynamic columns (skip 'subject' as primary column above, skip 'attachmentCount' as paperclip icon covers it) */}
        {gridColumns.filter(col => col.fieldId !== 'subject' && col.fieldId !== 'attachmentCount').map(col => {
          const val = resolveCellValue(correspondence, col, projects);
          const widthClass = col.width === 'wide' ? 'max-w-[200px]' : col.width === 'narrow' ? 'max-w-[100px]' : 'max-w-[150px]';
          // Special rendering for Response column - clickable links
          if (col.fieldId === 'responseStatus') {
            const rs = (correspondence as any).responseSummary;
            const responses: Array<{ responseId: string; subject?: string; status?: string }> = rs?.responses || [];
            if (responses.length === 0) {
              return (
                <td key={col.fieldId} className="hidden md:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                  -
                </td>
              );
            }
            return (
              <td key={col.fieldId} className="hidden md:table-cell px-4 py-3 text-sm">
                <div className="flex flex-wrap gap-1 items-center">
                  {responses.slice(0, 2).map((r: any) => {
                    const statusColor = r.status === 'approved' || r.status === 'closed'
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40'
                      : r.status === 'rejected'
                      ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40'
                      : 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40';
                    const id = r.responseId?._id || r.responseId;
                    const shortId = typeof id === 'string' ? id.slice(-6) : '';
                    return (
                      <button
                        key={id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/responses/${id}`); }}
                        className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors', statusColor)}
                        title={r.subject || `Response ${shortId}`}
                      >
                        {r.status || 'draft'}
                        {shortId && <span className="ml-1 opacity-60">#{shortId}</span>}
                      </button>
                    );
                  })}
                  {responses.length > 2 && (
                    <span className="text-xs text-gray-400">+{responses.length - 2}</span>
                  )}
                </div>
              </td>
            );
          }
          // Special rendering for multi-select custom fields
          if (col.type === 'multi-select') {
            const cf = (correspondence as any).customFields?.find((f: any) => f.fieldId === col.fieldId);
            const tags = Array.isArray(cf?.value) ? cf.value : [];
            return (
              <td key={col.fieldId} className="hidden md:table-cell px-4 py-3 text-sm text-gray-900 dark:text-white">
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 2).map((t: string) => (
                    <span key={t} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">{t}</span>
                  ))}
                  {tags.length > 2 && <span className="text-xs text-gray-400">+{tags.length - 2}</span>}
                  {tags.length === 0 && <span className="text-gray-400">-</span>}
                </div>
              </td>
            );
          }
          return (
            <td key={col.fieldId} className={cn("hidden md:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white", widthClass, "truncate")} title={val !== '-' ? val : undefined}>
              {val}
            </td>
          );
        })}
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={gridColumns.length + 1} className="p-0">
            <ExpandedRowDetails
              correspondence={correspondence}
              projects={projects}
              orgCustomFields={orgCustomFields}
              onUpdateProject={onUpdateProject}
              onUpdateCustomFields={onUpdateCustomFields}
              onUpdateDirection={onUpdateDirection}
              onUpdateStatus={onUpdateStatus}
              onDelete={onDelete}
              onCreateResponse={onCreateResponse}
            />
          </td>
        </tr>
      )}
    </>
  );
};

// ===== MULTI-SELECT DROPDOWN COMPONENT =====

const MultiSelectDropdown: React.FC<{
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[]; // Array of selected values
  onChange: (selected: string[]) => void;
  allLabel?: string;
}> = ({ label, options, selected, onChange, allLabel }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = () => onChange([]);

  // Display text
  const displayText = selected.length === 0
    ? (allLabel || `All ${label}`)
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full px-3 py-2 border rounded-md text-sm text-left flex items-center justify-between',
          selected.length > 0
            ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
        )}
      >
        <span className="truncate">{displayText}</span>
        <svg className={cn('w-4 h-4 ml-2 flex-shrink-0 transition-transform', open && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="w-full px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-left border-b border-gray-100 dark:border-gray-700"
            >
              Clear all
            </button>
          )}
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
                className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 dark:border-gray-500 focus:ring-blue-500 mr-2 flex-shrink-0"
              />
              <span className="text-gray-900 dark:text-white truncate">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== SYSTEM FILTER DEFINITIONS =====
// Maps system fieldIds to their filter config. Filters appear in this order,
// followed by any dynamic custom field filters from org customization.

const SYSTEM_FILTER_OPTIONS: Record<string, { label: string; filterKey: string; options: Array<{ value: string; label: string }> }> = {
  correspondenceStatus: {
    label: 'Status',
    filterKey: 'correspondenceStatus',
    options: [
      { value: 'open', label: 'Open' },
      { value: 'partially_closed', label: 'Partially Closed' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  projectName: {
    label: 'Project',
    filterKey: 'projectId',
    options: [], // Populated dynamically from projects list
  },
  documentType: {
    label: 'Document Type',
    filterKey: 'documentType',
    options: [
      { value: 'email', label: 'Email' },
      { value: 'letter', label: 'Letter' },
      { value: 'memo', label: 'Memo' },
      { value: 'report', label: 'Report' },
      { value: 'contract', label: 'Contract' },
      { value: 'invoice', label: 'Invoice' },
      { value: 'other', label: 'Other' },
    ],
  },
  attachmentCount: {
    label: 'Attachments',
    filterKey: 'hasAttachments',
    options: [
      { value: 'true', label: 'With Attachments' },
      { value: 'false', label: 'No Attachments' },
    ],
  },
};

// ===== FILTERS COMPONENT =====

const FiltersBar: React.FC<{
  filters: TableFilters;
  projects: Project[];
  filterableColumns: GridColumn[];
  onFilterChange: (filters: Partial<TableFilters>) => void;
  onReset: () => void;
}> = ({ filters, projects, filterableColumns, onFilterChange, onReset }) => {
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k !== 'search' && v !== '' && v !== 'all');

  // Build the list of filter dropdowns to render
  const filterDropdowns = useMemo(() => {
    const dropdowns: Array<{
      key: string;
      label: string;
      filterKey: string;
      options: Array<{ value: string; label: string }>;
    }> = [];

    // 1. Always show Status filter first
    dropdowns.push(SYSTEM_FILTER_OPTIONS.correspondenceStatus);

    // 2. Always show Direction filter
    dropdowns.push({
      key: 'direction',
      label: 'Direction',
      filterKey: 'direction',
      options: [
        { value: 'inward', label: 'Inward (Received)' },
        { value: 'outward', label: 'Outward (Sent)' },
        { value: 'internal', label: 'Internal' },
      ],
    });

    // 3. Always show Project filter
    dropdowns.push({
      key: 'projectId',
      label: 'Project',
      filterKey: 'projectId',
      options: [
        ...projects.filter(p => p.isActive !== false).map(p => ({
          value: (p as any)._id || p.id || '',
          label: (p as any).projectName || p.name || 'Unknown',
        })),
      ],
    });

    // 4. Add system filters from grid config (if filterable and not already added)
    const addedKeys = new Set(['correspondenceStatus', 'direction', 'projectId', 'projectName']);
    filterableColumns
      .filter(col => col.fieldType === 'system' && !addedKeys.has(col.fieldId))
      .forEach(col => {
        const systemDef = SYSTEM_FILTER_OPTIONS[col.fieldId];
        if (systemDef) {
          addedKeys.add(col.fieldId);
          dropdowns.push({ key: col.fieldId, ...systemDef });
        }
      });

    // 5. Add custom field filters (select/multi-select from org customization)
    filterableColumns
      .filter(col => col.fieldType === 'custom' && col.options && col.options.length > 0)
      .forEach(col => {
        dropdowns.push({
          key: col.fieldId,
          label: col.label,
          filterKey: `cf_${col.fieldId}`,
          options: col.options!.map(opt => ({ value: opt.value, label: opt.label })),
        });
      });

    return dropdowns;
  }, [filterableColumns, projects]);

  // Determine grid columns based on number of dropdowns
  const gridColsClass = filterDropdowns.length <= 3 ? 'md:grid-cols-3'
    : filterDropdowns.length <= 4 ? 'md:grid-cols-4'
    : filterDropdowns.length <= 5 ? 'md:grid-cols-5'
    : 'md:grid-cols-4 lg:grid-cols-6';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Icons.MagnifyingGlass />
              </div>
              <Input
                type="text"
                placeholder="Search correspondences..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center"
            >
              <span className="mr-2">
                <Icons.Funnel />
              </span>
              Filters
              {hasActiveFilters && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white rounded-full text-xs">
                  {Object.entries(filters).filter(([k, v]) => k !== 'search' && v !== '' && v !== 'all').length}
                </span>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="secondary" size="sm" onClick={onReset}>
                Reset
              </Button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className={`grid grid-cols-1 ${gridColsClass} gap-4 pt-4 border-t border-gray-200 dark:border-gray-700`}>
            {filterDropdowns.map(dd => (
              <div key={dd.filterKey}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {dd.label}
                </label>
                <MultiSelectDropdown
                  label={dd.label}
                  options={dd.options}
                  selected={(filters[dd.filterKey] || '').split(',').filter(Boolean)}
                  onChange={(vals) => onFilterChange({ [dd.filterKey]: vals.join(',') })}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== EXPORT MODAL =====

// Export job state that persists across modal open/close
interface ExportJobState {
  jobId: string | null;
  progress: { phase: string; percent: number; total?: number; processed?: number } | null;
  error: string | null;
  format: 'xlsx' | 'pdf';
}

const ExportModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  currentFilters: TableFilters;
  projects: Project[];
  exportJob: ExportJobState;
  onExportJobChange: (update: Partial<ExportJobState>) => void;
}> = ({ isOpen, onClose, currentFilters, projects, exportJob, onExportJobChange }) => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [includeAttachmentLinks, setIncludeAttachmentLinks] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Local editable filters as arrays for multi-select (initialized from currentFilters)
  const toArray = (v: string) => v ? v.split(',').filter(Boolean) : [];
  const [exportDirection, setExportDirection] = useState<string[]>(toArray(currentFilters.direction));
  const [exportStatus, setExportStatus] = useState<string[]>(toArray(currentFilters.correspondenceStatus));
  const [exportProjectId, setExportProjectId] = useState<string[]>(toArray(currentFilters.projectId));
  const [exportSearch, setExportSearch] = useState(currentFilters.search || '');

  // Sync local filters when page filters change and no job is active
  React.useEffect(() => {
    if (!exportJob.jobId) {
      setExportDirection(toArray(currentFilters.direction));
      setExportStatus(toArray(currentFilters.correspondenceStatus));
      setExportProjectId(toArray(currentFilters.projectId));
      setExportSearch(currentFilters.search || '');
    }
  }, [currentFilters.direction, currentFilters.correspondenceStatus, currentFilters.projectId, currentFilters.search, exportJob.jobId]);

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Resume polling if modal reopens with an active (non-complete, non-failed) job
  React.useEffect(() => {
    if (isOpen && exportJob.jobId && exportJob.progress && exportJob.progress.phase !== 'complete' && !exportJob.error) {
      if (pollRef.current) clearInterval(pollRef.current);
      const id = exportJob.jobId;
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await apiClient.correspondences.getExportJobStatus(id);
          const status = (statusRes as any)?.data || (statusRes as any);
          if (status.state === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current);
            onExportJobChange({ progress: { phase: 'complete', percent: 100, total: status.result?.total } });
          } else if (status.state === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            onExportJobChange({ error: status.error || 'Export failed', progress: null });
          } else if (status.progress) {
            onExportJobChange({ progress: status.progress });
          }
        } catch {
          // Ignore polling errors
        }
      }, 2000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isOpen, exportJob.jobId]);

  const handleExport = async () => {
    onExportJobChange({ error: null, progress: { phase: 'submitting', percent: 0 } });

    try {
      const payload: any = {
        format: exportJob.format,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        includeAttachmentLinks
      };

      // Use the modal's editable filters (join arrays as comma-separated for backend)
      if (exportDirection.length > 0) payload.direction = exportDirection.join(',');
      if (exportStatus.length > 0) payload.correspondenceStatus = exportStatus.join(',');
      if (exportProjectId.length > 0) payload.projectId = exportProjectId.join(',');
      if (exportSearch) payload.search = exportSearch;

      // Pass custom field filters from page
      Object.entries(currentFilters).forEach(([k, v]) => {
        if (k.startsWith('cf_') && v) payload[k] = v;
      });

      const res = await apiClient.correspondences.createExportJob(payload);
      const data = (res as any)?.data || (res as any);
      const id = data.jobId;
      if (!id) throw new Error('No job ID returned');

      onExportJobChange({ jobId: id, progress: { phase: 'queued', percent: 5 } });

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await apiClient.correspondences.getExportJobStatus(id);
          const status = (statusRes as any)?.data || (statusRes as any);

          if (status.state === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current);
            onExportJobChange({ progress: { phase: 'complete', percent: 100, total: status.result?.total } });
          } else if (status.state === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            onExportJobChange({ error: status.error || 'Export failed', progress: null });
          } else if (status.progress) {
            onExportJobChange({ progress: status.progress });
          }
        } catch {
          // Ignore polling errors, will retry
        }
      }, 2000);
    } catch (err: any) {
      onExportJobChange({ error: err.message || 'Failed to start export', progress: null });
    }
  };

  const handleDownload = async () => {
    if (!exportJob.jobId) return;
    setDownloading(true);
    try {
      const blob = await apiClient.correspondences.downloadExport(exportJob.jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `correspondences-${dateStr}.${exportJob.format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      onExportJobChange({ error: 'Download failed: ' + (err.message || 'Unknown error') });
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    // Don't kill the job — just hide the modal. Polling stops when modal closes (useEffect cleanup).
    onClose();
  };

  const handleReset = () => {
    // Reset the job state so user can start a new export
    if (pollRef.current) clearInterval(pollRef.current);
    onExportJobChange({ jobId: null, progress: null, error: null });
  };

  if (!isOpen) return null;

  const { progress, error, format } = exportJob;
  const isExporting = progress !== null && progress.phase !== 'complete';
  const isComplete = progress?.phase === 'complete';
  const hasActiveJob = exportJob.jobId !== null;

  const projectOptions = projects.filter(p => p.isActive !== false).map(p => ({
    value: (p as any)._id || p.id || '',
    label: (p as any).projectName || p.name || 'Unknown',
  }));

  const phaseLabels: Record<string, string> = {
    submitting: 'Starting export...',
    queued: 'Queued for processing...',
    counting: 'Counting records...',
    fetching: `Fetching records${progress?.processed ? ` (${progress.processed}/${progress.total})` : ''}...`,
    generating: 'Generating file...',
    complete: `Export complete! ${progress?.total || 0} records exported.`
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Export Correspondences</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range *</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={hasActiveJob}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={hasActiveJob}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          {/* Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filters</label>
            <div className={cn('grid grid-cols-2 gap-3', hasActiveJob && 'pointer-events-none opacity-60')}>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Direction</label>
                <MultiSelectDropdown
                  label="Direction"
                  options={[
                    { value: 'inward', label: 'Inward (Received)' },
                    { value: 'outward', label: 'Outward (Sent)' },
                    { value: 'internal', label: 'Internal' },
                  ]}
                  selected={exportDirection}
                  onChange={setExportDirection}
                  allLabel="All Directions"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Status</label>
                <MultiSelectDropdown
                  label="Status"
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'partially_closed', label: 'Partially Closed' },
                    { value: 'closed', label: 'Closed' },
                  ]}
                  selected={exportStatus}
                  onChange={setExportStatus}
                  allLabel="All Statuses"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Project</label>
                <MultiSelectDropdown
                  label="Project"
                  options={projectOptions}
                  selected={exportProjectId}
                  onChange={setExportProjectId}
                  allLabel="All Projects"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Search</label>
                <input
                  type="text"
                  value={exportSearch}
                  onChange={(e) => setExportSearch(e.target.value)}
                  disabled={hasActiveJob}
                  placeholder="Search text..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Format</label>
            <div className="flex gap-3">
              {(['xlsx', 'pdf'] as const).map(f => (
                <label key={f} className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors text-sm',
                  format === f
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                )}>
                  <input type="radio" name="format" checked={format === f} onChange={() => onExportJobChange({ format: f })} disabled={hasActiveJob} className="sr-only" />
                  {f === 'xlsx' ? '📊 Excel (.xlsx)' : '📄 PDF'}
                </label>
              ))}
            </div>
          </div>

          {/* Include attachment links */}
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={includeAttachmentLinks}
              onChange={(e) => setIncludeAttachmentLinks(e.target.checked)}
              disabled={hasActiveJob}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Include attachment download links
          </label>

          {/* Progress */}
          {progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">{phaseLabels[progress.phase] || progress.phase}</span>
                <span className="font-medium text-gray-900 dark:text-white">{progress.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className={cn(
                    'h-2.5 rounded-full transition-all duration-500',
                    isComplete ? 'bg-green-500' : 'bg-blue-500'
                  )}
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          {(isComplete || error) && (
            <Button variant="secondary" size="sm" onClick={handleReset}>
              New Export
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={handleClose}>
            {isComplete ? 'Close' : isExporting ? 'Minimize' : 'Cancel'}
          </Button>
          {isComplete ? (
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {downloading ? 'Downloading...' : `Download ${format.toUpperCase()}`}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleExport}
              disabled={isExporting || !startDate || !endDate}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isExporting ? 'Exporting...' : 'Start Export'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ===== MAIN PAGE COMPONENT =====

export const CorrespondencesPageV3: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { addNotification } = useAppStore();

  // State
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TableFilters>({
    search: '',
    projectId: '',
    documentType: '',
    hasAttachments: '',
    hasProject: '',
    direction: '',
    correspondenceStatus: ''
  });

  // Create Response Modal
  const [createResponseModalOpen, setCreateResponseModalOpen] = useState(false);
  const [selectedCorrespondenceForResponse, setSelectedCorrespondenceForResponse] = useState<Correspondence | null>(null);

  // Export Modal + persistent job state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportJob, setExportJob] = useState<ExportJobState>({
    jobId: null,
    progress: null,
    error: null,
    format: 'xlsx'
  });
  const handleExportJobChange = (update: Partial<ExportJobState>) => {
    setExportJob(prev => ({ ...prev, ...update }));
  };
  const exportInProgress = exportJob.jobId !== null && exportJob.progress !== null && exportJob.progress.phase !== 'complete';
  const exportReady = exportJob.progress?.phase === 'complete';

  // Debounce search
  const debouncedSearch = useDebounced(filters.search, 500);

  // Fetch data
  const { fields: orgCustomFields, columns: gridColumns, filterableColumns } = useOrgCustomization();
  // Full (unpaginated) project list for the filter dropdown — the paginated
  // projects API would otherwise cap the list and hide projects from filters.
  const { data: projectsData, loading: projectsLoading } = useAllProjects();
  const projects = projectsData?.data || [];

  // Build query params
  const queryParams = useMemo(() => {
    const params: any = {
      page,
      limit: 20,
      sortBy: sortField === 'from' ? 'createdAt' : sortField,
      sortOrder: sortOrder,
      search: debouncedSearch || undefined,  // backend expects `search`, not `q`
      projectId: filters.projectId === 'none' ? undefined : filters.projectId || undefined,
      hasProject: filters.hasProject || undefined,
      documentType: filters.documentType || undefined,
      hasAttachments: filters.hasAttachments || undefined,
      direction: filters.direction || undefined,
      correspondenceStatus: filters.correspondenceStatus || undefined,
      // Dynamic custom field filters (cf_* keys)
      ...Object.fromEntries(
        Object.entries(filters)
          .filter(([k, v]) => k.startsWith('cf_') && v)
          .map(([k, v]) => [k, v])
      )
    };

    // Remove undefined values
    return Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined));
  }, [page, sortField, sortOrder, debouncedSearch, filters]);

  const {
    data: correspondencesData,
    loading: correspondencesLoading,
    error: correspondencesError,
    refetch: refetchCorrespondences
  } = useCorrespondences(queryParams);

  const correspondences = correspondencesData?.data || [];
  const pagination = correspondencesData?.pagination;

  // Mutations
  const { mutate: updateCorrespondence, loading: updating } = useUpdateCorrespondence();
  const { mutate: deleteCorrespondence, loading: deleting } = useDeleteCorrespondence();

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (newFilters: Partial<TableFilters>) => {
    setFilters({ ...filters, ...newFilters });
    setPage(1); // Reset to first page
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      projectId: '',
      documentType: '',
      hasAttachments: '',
      hasProject: '',
      direction: '',
      correspondenceStatus: ''
    });
    setPage(1);
  };

  const handleUpdateProject = async (id: string, projectId: string | null) => {
    try {
      await updateCorrespondence({ id, data: { projectId: projectId || undefined } });
      refetchCorrespondences();
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const handleUpdateDirection = async (id: string, direction: 'inward' | 'outward' | 'internal') => {
    try {
      await updateCorrespondence({ id, data: { direction } });
      refetchCorrespondences();
      addNotification({ type: 'success', title: 'Direction updated', message: `Marked as ${direction}.` });
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to update direction';
      addNotification({ type: 'error', title: 'Update failed', message: msg });
    }
  };

  const handleUpdateStatus = async (id: string, correspondenceStatus: 'open' | 'partially_closed' | 'closed') => {
    try {
      await updateCorrespondence({ id, data: { correspondenceStatus } });
      refetchCorrespondences();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Failed to update status';
      addNotification({ type: 'error', title: 'Update failed', message: msg });
    }
  };

  const handleUpdateCustomFields = async (id: string, customFields: any[]) => {
    try {
      await updateCorrespondence({ id, data: { customFields } });
      refetchCorrespondences();
    } catch (error) {
      console.error('Failed to update custom fields:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCorrespondence(id);
      if (expandedRowId === id) {
        setExpandedRowId(null);
      }
      addNotification({ type: 'success', title: 'Deleted', message: 'Correspondence deleted successfully' });
      refetchCorrespondences();
    } catch (error: any) {
      console.error('Failed to delete correspondence:', error);
      const msg = error?.response?.data?.error || error?.message || 'Failed to delete correspondence';
      addNotification({ type: 'error', title: 'Delete Failed', message: msg });
    }
  };

  const handleCreateResponse = (correspondence: Correspondence) => {
    setSelectedCorrespondenceForResponse(correspondence);
    setCreateResponseModalOpen(true);
  };

  const handleToggleRow = (id: string) => {
    setExpandedRowId(expandedRowId === id ? null : id);
  };

  // Loading state
  if (correspondencesLoading && correspondences.length === 0) {
    return (
      <PageLayout title="Correspondences">
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Correspondences"
      actions={
        <div className="flex items-center space-x-2">
          <Button variant="secondary" onClick={() => setShowExportModal(true)} className="relative">
            <span className="mr-2">
              <Icons.ArrowDownTray />
            </span>
            Export
            {exportInProgress && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse" title="Export in progress" />
            )}
            {exportReady && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" title="Export ready to download" />
            )}
          </Button>
          <Button onClick={() => navigate('/emails')}>
            <span className="mr-2">
              <Icons.Envelope />
            </span>
            Go to Emails
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Direction Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-1">Direction:</span>
          <button
            onClick={() => handleFilterChange({ direction: '' })}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
              filters.direction === ''
                ? 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
            )}
          >
            All
          </button>
          <button
            onClick={() => handleFilterChange({ direction: 'inward' })}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
              filters.direction === 'inward'
                ? 'bg-cyan-50 text-cyan-700 border-cyan-300 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-700'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
            )}
          >
            Inward (Received)
          </button>
          <button
            onClick={() => handleFilterChange({ direction: 'outward' })}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
              filters.direction === 'outward'
                ? 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
            )}
          >
            Outward (Sent)
          </button>
          <button
            onClick={() => handleFilterChange({ direction: 'internal' })}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors border',
              filters.direction === 'internal'
                ? 'bg-gray-100 text-gray-700 border-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-500'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
            )}
          >
            Internal
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {pagination?.totalItems || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Correspondences</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
            <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
              {correspondences.filter((c: any) => c.direction === 'inward').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Inward (Received)</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {correspondences.filter((c: any) => c.direction === 'outward').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Outward (Sent)</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {correspondences.filter(c => getAttachmentCount(c) > 0).length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">With Attachments</div>
          </div>
        </div>

        {/* Filters */}
        <FiltersBar
          filters={filters}
          projects={projects}
          filterableColumns={filterableColumns}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
        />

        {/* Error */}
        {correspondencesError && (
          <ErrorAlert message={typeof correspondencesError === 'string' ? correspondencesError : (correspondencesError as any)?.message || 'Failed to load correspondences'} />
        )}

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 w-10"></th>
                  {/* Subject is always the first column (primary) */}
                  <TableHeader
                    field="subject"
                    label="Subject"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  {/* Dynamic columns from grid config + custom fields (skip attachmentCount — paperclip icon covers it) */}
                  {gridColumns.filter(col => col.fieldId !== 'subject' && col.fieldId !== 'attachmentCount').map(col => {
                    const sortable = col.isSortable && ['from', 'documentDate', 'documentType', 'createdAt', 'projectName'].includes(col.fieldId);
                    if (sortable) {
                      return (
                        <TableHeader
                          key={col.fieldId}
                          field={col.fieldId as SortField}
                          label={col.label}
                          currentField={sortField}
                          currentOrder={sortOrder}
                          onSort={handleSort}
                          className="hidden md:table-cell"
                        />
                      );
                    }
                    return (
                      <th key={col.fieldId} className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        {col.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {correspondences.length === 0 ? (
                  <tr>
                    <td colSpan={gridColumns.length + 1}>
                      <EmptyState
                        icon={<MessageSquare size={24} />}
                        title="No correspondences found"
                        description="Try adjusting your filters or search criteria to find what you're looking for."
                      />
                    </td>
                  </tr>
                ) : (
                  correspondences.map((correspondence) => (
                    <TableRow
                      key={correspondence._id || correspondence.id}
                      correspondence={correspondence}
                      projects={projects}
                      orgCustomFields={orgCustomFields}
                      gridColumns={gridColumns}
                      isExpanded={expandedRowId === (correspondence._id || correspondence.id)}
                      onToggle={() => handleToggleRow(correspondence._id || correspondence.id || '')}
                      onUpdateProject={handleUpdateProject}
                      onUpdateCustomFields={handleUpdateCustomFields}
                      onUpdateDirection={handleUpdateDirection}
                      onUpdateStatus={handleUpdateStatus}
                      onDelete={handleDelete}
                      onCreateResponse={handleCreateResponse}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, pagination.totalItems)} of {pagination.totalItems} results
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1 || correspondencesLoading}
                >
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        disabled={correspondencesLoading}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  {pagination.totalPages > 5 && <span className="px-2">...</span>}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages || correspondencesLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        currentFilters={filters}
        projects={projects}
        exportJob={exportJob}
        onExportJobChange={handleExportJobChange}
      />

      {/* Create Response Modal */}
      {selectedCorrespondenceForResponse && (
        <CreateResponseModal
          isOpen={createResponseModalOpen}
          onClose={() => {
            setCreateResponseModalOpen(false);
            setSelectedCorrespondenceForResponse(null);
          }}
          correspondence={selectedCorrespondenceForResponse}
          onSuccess={() => {
            setCreateResponseModalOpen(false);
            setSelectedCorrespondenceForResponse(null);
            refetchCorrespondences();
          }}
        />
      )}
    </PageLayout>
  );
};

export default CorrespondencesPageV3;
